import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  UserCheck, Download, Search, RefreshCw, Loader2, QrCode, Barcode, MousePointerClick, Users, CheckCircle2, Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 8000;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};

const fmtClock = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' }); }
  catch { return ''; }
};

const MethodPill = ({ method }) => {
  const m = (method || 'barcode').toLowerCase();
  if (m === 'qr') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded">
        <QrCode className="w-3 h-3" /> QR
      </span>
    );
  }
  if (m === 'manual') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
        <MousePointerClick className="w-3 h-3" /> Manual
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200 px-1.5 py-0.5 rounded">
      <Barcode className="w-3 h-3" /> Barcode
    </span>
  );
};

/**
 * Check-In Log — live-updating list of attendees who have been successfully
 * checked in. Renders inside the Admin Events tab beneath Attendee Management.
 *
 * Polls /api/admin/events/checkin-feed every 8s. Highlights newly arrived rows
 * for 6 seconds with a soft green flash so volunteers can confirm the latest
 * scan registered.
 */
const CheckInLogPanel = ({ events = [], onCheckIn }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [newlyArrivedIds, setNewlyArrivedIds] = useState(new Set());

  const fetchFeed = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (eventFilter) params.set('event_id', eventFilter);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '200');
      const r = await axios.get(`${BACKEND_URL}/api/admin/events/checkin-feed?${params}`, { withCredentials: true });
      // Detect newly arrived rows (compare with previous data)
      setData(prev => {
        if (prev?.rows?.length) {
          const prevIds = new Set(prev.rows.map(row => row.entry_id + (row.checked_in_at || '')));
          const newIds = new Set();
          for (const row of r.data.rows) {
            const key = row.entry_id + (row.checked_in_at || '');
            if (!prevIds.has(key)) newIds.add(key);
          }
          if (newIds.size > 0) {
            setNewlyArrivedIds(curr => new Set([...curr, ...newIds]));
            setTimeout(() => {
              setNewlyArrivedIds(curr => {
                const next = new Set(curr);
                for (const id of newIds) next.delete(id);
                return next;
              });
            }, 6000);
          }
        }
        return r.data;
      });
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load check-in log');
    } finally {
      setLoading(false);
    }
  }, [eventFilter, search]);

  useEffect(() => {
    fetchFeed();
    const t = setInterval(fetchFeed, POLL_MS);
    return () => clearInterval(t);
  }, [fetchFeed]);

  // Refresh when parent reports a new check-in occurred (e.g., from CheckInScanner)
  useEffect(() => {
    if (onCheckIn) onCheckIn.current = fetchFeed;
  }, [onCheckIn, fetchFeed]);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (eventFilter) params.set('event_id', eventFilter);
    window.location.href = `${BACKEND_URL}/api/admin/events/checkin-logs/export?${params}`;
  };

  const summary = data?.summary || { total_rsvps: 0, total_checked_in: 0, remaining: 0 };
  const rows = data?.rows || [];

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="checkin-log-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-sm flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-green-700" />
          Check-In Log
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> LIVE
          </span>
        </h3>
        <div className="flex gap-2 flex-wrap">
          <button onClick={fetchFeed} disabled={loading}
            className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-1.5 hover:bg-[#F1F5F9] flex items-center gap-1 disabled:opacity-50"
            data-testid="checkin-log-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={handleExport}
            className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-1.5 hover:bg-[#F1F5F9] flex items-center gap-1"
            data-testid="checkin-log-export-csv-btn">
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>
      </div>

      {/* SUMMARY ROW */}
      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="checkin-log-summary">
        <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-3 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Total RSVPs</p>
            <p className="font-heading text-xl font-bold" data-testid="summary-total-rsvps">{summary.total_rsvps}</p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-green-800 font-bold">Checked In</p>
            <p className="font-heading text-xl font-bold text-green-700" data-testid="summary-total-checked-in">{summary.total_checked_in}</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Remaining</p>
            <p className="font-heading text-xl font-bold text-amber-700" data-testid="summary-remaining">{summary.remaining}</p>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input type="text" placeholder="Search name, email, or Entry ID..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="checkin-log-search-input" />
        </div>
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
          className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
          data-testid="checkin-log-event-filter">
          <option value="">All Events</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs mb-2">{error}</div>}

      {/* TABLE */}
      {rows.length === 0 ? (
        <div className="text-center py-10 text-[#9CA3AF] text-sm" data-testid="checkin-log-empty">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
            'No check-ins yet. As volunteers scan QR codes or barcodes, attendees will appear here in real time.'}
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E5E7EB] rounded">
          <table className="w-full text-xs" data-testid="checkin-log-table">
            <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-3 py-2 font-bold">Attendee</th>
                <th className="px-3 py-2 font-bold">Entry ID</th>
                <th className="px-3 py-2 font-bold hidden md:table-cell">Email</th>
                <th className="px-3 py-2 font-bold hidden lg:table-cell">Event</th>
                <th className="px-3 py-2 font-bold">Check-In Time</th>
                <th className="px-3 py-2 font-bold">Method</th>
                <th className="px-3 py-2 font-bold hidden md:table-cell">By</th>
                <th className="px-3 py-2 font-bold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {rows.map((row) => {
                const key = row.entry_id + (row.checked_in_at || '');
                const isNew = newlyArrivedIds.has(key);
                return (
                  <tr key={key} className={`${isNew ? 'bg-green-50 transition-colors duration-700' : 'hover:bg-[#F8F9FA]'}`}
                    data-testid={`checkin-log-row-${row.entry_id}`}>
                    <td className="px-3 py-2 font-medium text-[#0A0A0A]">{row.attendee_name || '—'}</td>
                    <td className="px-3 py-2 font-mono text-[#4B5563]">{row.entry_id}</td>
                    <td className="px-3 py-2 text-[#4B5563] hidden md:table-cell truncate max-w-[180px]" title={row.attendee_email}>{row.attendee_email || '—'}</td>
                    <td className="px-3 py-2 text-[#4B5563] hidden lg:table-cell truncate max-w-[180px]" title={row.event_name}>{row.event_name || '—'}</td>
                    <td className="px-3 py-2 text-[#4B5563]" title={fmtClock(row.checked_in_at)}>
                      <div className="leading-tight">
                        <div className="font-mono">{fmtClock(row.checked_in_at)}</div>
                        <div className="text-[10px] text-[#9CA3AF]">{fmtTime(row.checked_in_at)}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2"><MethodPill method={row.method} /></td>
                    <td className="px-3 py-2 text-[#4B5563] hidden md:table-cell truncate max-w-[140px]" title={row.checked_in_by}>{row.checked_in_by || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 rounded">
                        <CheckCircle2 className="w-3 h-3" /> Checked In
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] mt-2">
        Live-updating every {POLL_MS / 1000}s · {rows.length} successful check-in{rows.length === 1 ? '' : 's'} shown
        {data?.as_of && <> · last refresh {fmtClock(data.as_of)}</>}
      </p>
    </div>
  );
};

export default CheckInLogPanel;
